import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useMatch } from "react-router-dom";

import {
  AboutTheRoomDetails,
  AboutTheRoomRow,
  AboutTheRoomSection,
  AvailabilityAndPricesSection,
  DetailsSection,
  HostDetailsAbout,
  HostDetailsInfoSection,
  HostDetailsWrapper,
  LeftColumnAbout,
  Location,
  MainBody,
  MainWrapper,
  MapWithRoomImage,
  MapWithRoomImageCaption,
  MapWithRoomRoot,
  RightColumnAbout,
  RoomDetailsDescription,
  RoomDetailsSection,
  RoomNavigationSection,
  NavigationSpan,
  SectionTitle,
  SocialPreferensecRange,
  TypeOfRoom
} from "./styles";
import { InfoMessage } from "../HostHouseRegistration/RoomsDetails/styles";
import {
  Button,
  IcoArrowRight,
  IcoInfo,
  TitleH2,
  TitleH3,
  TitleH4
} from "components/generic";

import {
  AboutMeView,
  SocialPreferences
} from "pages/ProfileUser/SimpleComponents";
import { useRange } from "components/Range";
import { Carousel } from "./Carousel";
import { HostVerify } from "pages/ProfileUserEdit/HostVerified";
import { IHouse, IMatch, IUserShort, THouseType } from "interfaces/intarfaces";
import { MONTHS, ROOM_INFO_ROUTE } from "utils/constants";
import { theme } from "styles/theme";
import { UserAPI } from "API/userApi";
import { LoaderCustom } from "components/Loader";
import { activeYears } from "pages/ProfileUser/StudentAboutInfo";
import { useContextHospi } from "../../context/ContextHospi";
import { Button as AntdButton, Skeleton } from "antd";
import { ShowInterestModal } from "../../components/ShowInterestModal";
import { useGetMatch } from "../../API/queries/studentApiQueries";
import { useIsHost } from "../../utils/helpers";
import { HouseAPI } from "../../API/houseApi";
import {
  generateGoogleMapsUrl,
  generateStaticMapImageUrl
} from "../../components/Map/utils";

// map types //

// MAIN COMPONENT //

export const DetailedRoomInfo: React.FC<{
  roomId?: string;
  setShowRoomPreview?: (show: boolean) => void;
}> = ({ roomId, setShowRoomPreview }) => {
  const [room, setRoom] = useState<Partial<IHouse> | undefined>(undefined);
  const [host, setHost] = useState<Partial<IUserShort>>({});
  const { userProfile } = useContextHospi();
  const [showInterestModal, setShowInterestModal] = useState(false);
  const isHost = useIsHost();

  const { t } = useTranslation("student");
  const routeMatch = useMatch(ROOM_INFO_ROUTE + "/:id"); // useParams not working here by some reason
  const navigate = useNavigate();

  const { data: match, isLoading: isLoadingMatch } = useGetMatch(
    userProfile?.user_id,
    host.user_id,
    room?.house_id,
    !isHost
  );

  useEffect(() => {
    const currentRoomId = roomId ? roomId : routeMatch?.params?.id;
    if (!currentRoomId) {
      return;
    }
    HouseAPI.getHouse(currentRoomId).then((house) => {
      if (Object.keys(house).includes("title")) {
        setRoom(house);
      }
    });
  }, [roomId, routeMatch?.params?.id]);

  useEffect(() => {
    if (room?.user_id !== undefined) {
      UserAPI.getUserPreview(room?.user_id).then((user) => {
        setHost(user);
      });
    }
  }, [room]);

  const roomPhotos = useMemo(() => {
    return room?.photos?.sort((a, b) => a.order - b.order) ?? [];
  }, [room?.photos]);

  const roomLocation = useMemo(() => {
    if (room?.latitude !== undefined && room?.longitude !== undefined) {
      return {
        lat: room.latitude,
        lng: room.longitude
      };
    }
    return null;
  }, [room?.latitude, room?.longitude]);

  return (
    <MainWrapper>
      {setShowRoomPreview && (
        <button id="handle-close" onClick={() => setShowRoomPreview(false)}>
          &#10005;
        </button>
      )}
      {!room ? (
        <LoaderCustom
          size={5}
          color={theme.colors.bgBody}
          text={t("textLoading.0")}
        />
      ) : (
        <>
          {!isHost ? (
            <RoomNavigation
              title={room.title ?? ""}
              street={room.street ?? ""}
              city={room.city ?? ""}
              hostName={`${host.first_name} ${host.last_name}`}
              displayShowInterestButton={!isHost}
              match={match}
              isLoadingMatch={isLoadingMatch}
              onShowInterestClick={() => {
                setShowInterestModal(true);
              }}
            />
          ) : null}
          {roomPhotos.length ? <Carousel photos={roomPhotos} /> : ""}
          <MainBody>
            <LeftColumnAbout>
              <DetailsSection>
                <RoomDetails
                  description={room.description ?? ""}
                  kind={(room.kind as THouseType) ?? ""}
                  city={room.city ?? ""}
                  street={room.street ?? ""}
                />
                <AboutTheRoom
                  bedroom_area={room.bedroom_area ?? 0}
                  house_area={room.house_area ?? 0}
                  shared_items={room.shared_items ?? []}
                  private_items={room.private_items ?? []}
                />
                <AvailabilityAndPrices
                  available_from={room.available_from ?? ""}
                  available_to={room.available_to ?? ""}
                  price={room.price ?? 0}
                  additional_expenses={room.additional_expenses ?? 0}
                  deposit={room.deposit ?? 0}
                />
              </DetailsSection>
            </LeftColumnAbout>
            <RightColumnAbout>
              {host === undefined ? (
                ""
              ) : (
                <>
                  <HostDetails
                    verification_status={host.verification_status!}
                    first_name={host.first_name}
                    last_name={host.last_name}
                    active_years={host.active_years ?? 0}
                    hosted_tenants_count={host.hosted_tenants_count ?? 0}
                    thumbnail_url={host.thumbnail_url}
                  />
                  <AboutTheHost
                    gender={host.gender}
                    spoken_languages={host.spoken_languages}
                    description={host.description}
                  />
                  <SocialPreferencesSection
                    privacy={host.privacy ?? 50}
                    requested_autonomy={host.requested_autonomy}
                  />
                </>
              )}
            </RightColumnAbout>
          </MainBody>
          {roomLocation ? (
            <MapWithRoom location={roomLocation} zoom={15} />
          ) : null}
        </>
      )}
      <Button
        main
        style={{
          alignSelf: "flex-end",
          margin: "1rem",
          padding: "16px 40px",
          fontSize: "18px"
        }}
        onClick={() => {
          if (setShowRoomPreview) {
            setShowRoomPreview(false);
          } else {
            window.history.length > 1 ? navigate(-1) : window.close();
          }
        }}
      >
        {t("buttonClose")}
      </Button>
      {showInterestModal && host?.user_id && room ? (
        <ShowInterestModal
          host={host}
          house={room}
          isShow={true}
          onClose={() => {
            setShowInterestModal(false);
          }}
          onSent={() => {
            setShowInterestModal(false);
          }}
        />
      ) : null}
    </MainWrapper>
  );
};

// NAVIGATION //

const RoomNavigation: React.FC<
  Pick<IHouse, "title" | "street" | "city"> & {
    onShowInterestClick: () => void;
    displayShowInterestButton: boolean;
    match?: IMatch;
    isLoadingMatch: boolean;
    hostName: string;
  }
> = ({
  title,
  city,
  displayShowInterestButton,
  onShowInterestClick,
  match,
  isLoadingMatch,
  hostName
}) => {
  const { t } = useTranslation("student");
  const navigate = useNavigate();
  return (
    <RoomNavigationSection>
      <NavigationSpan className="my-rooms" onClick={() => navigate(-1)}>
        {t("myRooms.pageTitle")}
      </NavigationSpan>
      <IcoArrowRight size=".8em" color={theme.colors.text} />

      <div className="title-city-container">
      <NavigationSpan>{`${title} - ${city}`}</NavigationSpan>
    </div>
      <div
        style={{
          display: "flex",
          flexGrow: 1,
          justifyContent: "flex-end"
        }}
      >
        {isLoadingMatch ? <Skeleton.Button size="large" active /> : null}
        {!isLoadingMatch && displayShowInterestButton && match ? (
          <>
            {match.match_status === undefined ? (
              <AntdButton
                type="primary"
                size="large"
                onClick={() => {
                  onShowInterestClick();
                }}
              >
                {t("roomNavigation.sendChat", { name: hostName })}
              </AntdButton>
            ) : (
              <AntdButton type="primary" size="large" disabled>
                {t("roomNavigation.chatRequestSent", { name: hostName })}
              </AntdButton>
            )}
          </>
        ) : null}
      </div>
    </RoomNavigationSection>
  );
};

// LEFT COLUMN //

const RoomDetails: React.FC<
  Pick<IHouse, "description" | "kind" | "street" | "city">
> = ({ description, kind, city }) => {
  const { t } = useTranslation("hostrooms");

  return (
    <RoomDetailsSection>
      <SectionTitle>
        <TitleH2>{t("roomsDetails.title")}</TitleH2>
      </SectionTitle>
      <RoomDetailsDescription>{description}</RoomDetailsDescription>
      <TypeOfRoom>
        <TitleH3>{t("roomsDetails.typeRoom.title")}</TitleH3>
        <TitleH4>
          {t(`roomsDetails.typeRoom.items.${kind}.title`)}{" "}
          <InfoMessage
            left="-2em"
            message={t(`roomsDetails.typeRoom.items.${kind}.tooltip`)}
          >
            <IcoInfo size="1.2em" />
          </InfoMessage>
        </TitleH4>
      </TypeOfRoom>
      <Location>
        <div>
          <TitleH3>{t("roomsDetails.cityRoom.title")}</TitleH3>
          <TitleH4>{city}</TitleH4>
        </div>
      </Location>
    </RoomDetailsSection>
  );
};

const AboutTheRoom: React.FC<
  Pick<IHouse, "bedroom_area" | "house_area" | "shared_items" | "private_items">
> = ({ bedroom_area, house_area, shared_items, private_items }) => {
  const { t } = useTranslation("hostrooms");

  return (
    <AboutTheRoomSection>
      <SectionTitle>
        <TitleH2>{t("aboutTheRoom.title")}</TitleH2>
      </SectionTitle>
      <AboutTheRoomRow>
        <div>
          <TitleH3>{t("aboutTheRoom.bedroomSize.title")}</TitleH3>
          <TitleH4>{bedroom_area}</TitleH4>
        </div>
        <div>
          <TitleH3>{t("aboutTheRoom.houseSize.title")}</TitleH3>
          <TitleH4>{house_area}</TitleH4>
        </div>
      </AboutTheRoomRow>
      <AboutTheRoomRow>
        <div>
          <TitleH3>{t("aboutTheRoom.sharedUse.title")}</TitleH3>
          {shared_items?.map((item: string, index: number) => (
            <TitleH4 key={index}>
              {t(`aboutTheRoom.sharedUse.items.${item}.title`)}
            </TitleH4>
          ))}
        </div>
        <div>
          <TitleH3>{t("aboutTheRoom.privateUse.title")}</TitleH3>
          {private_items?.map((item: string, index: number) => (
            <TitleH4 key={index}>
              {t(`aboutTheRoom.privateUse.items.${item}.title`)}
            </TitleH4>
          ))}
        </div>
      </AboutTheRoomRow>
    </AboutTheRoomSection>
  );
};

export const AvailabilityAndPrices: React.FC<
  Pick<
    IHouse,
    | "available_from"
    | "available_to"
    | "price"
    | "deposit"
    | "additional_expenses"
  >
> = ({ available_from, available_to, price, deposit, additional_expenses }) => {
  const { t: t_calendar } = useTranslation("translation");
  const { t } = useTranslation("hostrooms");

  const dateLine = (value: string | undefined): string => {
    if (value === undefined || value === null) return "";
    const dt = new Date(value);
    return `${dt.getDate()} ${t_calendar(
      `calendar.months.${MONTHS[dt.getMonth()]}`
    ).toLocaleLowerCase()} ${dt.getFullYear()}`;
  };

  return (
    <AvailabilityAndPricesSection>
      <SectionTitle>
        <TitleH2>{t("availabilityPrices.title")}</TitleH2>
      </SectionTitle>
      <AboutTheRoomDetails>
        <AboutTheRoomRow>
          <div>
            <TitleH3>{t("availabilityPrices.items.dateFrom.title")}</TitleH3>
            <TitleH4>{available_from ? dateLine(available_from) : "-"}</TitleH4>
          </div>
          <div>
            <TitleH3>{t("availabilityPrices.items.dateUntil.title")}</TitleH3>
            <TitleH4>
              {available_to === "2101-01-01"
                ? t("anyDate")
                : available_to
                ? dateLine(available_to)
                : "-"}
            </TitleH4>
          </div>
        </AboutTheRoomRow>
        <AboutTheRoomRow>
          <div>
            <TitleH3>{t("availabilityPrices.items.roomPrice.title")}</TitleH3>
            <TitleH4>{`€ ${price}`}</TitleH4>
          </div>
          <div>
            <TitleH3>{t("availabilityPrices.items.deposit.title")}</TitleH3>
            <TitleH4>{`€ ${deposit}`}</TitleH4>
          </div>
        </AboutTheRoomRow>
        <AboutTheRoomRow>
          <div>
            <TitleH3>
              {t("availabilityPrices.items.additionalCosts.title")}{" "}
              <InfoMessage
                left="-6em"
                message={t("availabilityPrices.items.additionalCosts.tooltip")}
              >
                <IcoInfo size="1.2em" />
              </InfoMessage>
            </TitleH3>
            <TitleH4>{`€ ${additional_expenses}`}</TitleH4>
          </div>
        </AboutTheRoomRow>
      </AboutTheRoomDetails>
    </AvailabilityAndPricesSection>
  );
};

// RIGHT COLUMN //

const HostDetails: React.FC<
  Pick<
    IUserShort,
    | "first_name"
    | "last_name"
    | "active_years"
    | "hosted_tenants_count"
    | "thumbnail_url"
    | "verification_status"
  >
> = ({
  first_name,
  last_name,
  active_years,
  hosted_tenants_count,
  thumbnail_url,
  verification_status
}) => {
  const { t } = useTranslation("host");
  return (
    <HostDetailsWrapper>
      <SectionTitle>
        <TitleH2>{t("aboutHost.title1")}</TitleH2>
        {verification_status &&
        hosted_tenants_count &&
        hosted_tenants_count > 0 ? (
          <HostVerify verification_status={"experienced"} />
        ) : (
          <span />
        )}
        <HostVerify verification_status={"verified"} showLabel={false} />
      </SectionTitle>
      <HostDetailsInfoSection>
        <HostDetailsAbout>
          <TitleH4>{`${first_name} ${last_name}`}</TitleH4>
          <div>
            <TitleH4>
              <strong>{activeYears(active_years)}</strong>{" "}
              {` ${t("aboutHost.yearsActive")}`}
            </TitleH4>
            <TitleH4>
              <strong>{hosted_tenants_count}</strong>{" "}
              {` ${t("aboutHost.timesHosted")}`}
            </TitleH4>
          </div>
        </HostDetailsAbout>
        <img src={thumbnail_url + `?${new Date().toString()}`} alt="" />
      </HostDetailsInfoSection>
    </HostDetailsWrapper>
  );
};

const AboutTheHost: React.FC<
  Pick<IUserShort, "description" | "gender" | "spoken_languages">
> = ({ gender, spoken_languages, description }) => {
  const { t } = useTranslation("host");
  return (
    <HostDetailsWrapper>
      <SectionTitle>
        <TitleH2>{t("aboutHost.title2")}</TitleH2>
      </SectionTitle>
      <AboutMeView
        descriptionUser={description}
        genderUser={gender}
        spokenLanguage={spoken_languages}
      />
    </HostDetailsWrapper>
  );
};

const SocialPreferencesSection: React.FC<
  Pick<IUserShort, "privacy" | "requested_autonomy">
> = ({ privacy, requested_autonomy }) => {
  const { t } = useTranslation("host");
  const privacyHost = useRange(privacy);
  const newInTown = useRange(requested_autonomy);

  useEffect(() => {
    privacyHost.onChange(privacy ?? 50);
    newInTown.onChange(requested_autonomy ?? 50);
  }, [privacy, requested_autonomy, privacyHost, newInTown]);

  return (
    <HostDetailsWrapper>
      <SectionTitle>
        <TitleH2>{t("hostProfile.socialPreferences.title")}</TitleH2>
      </SectionTitle>
      <SocialPreferensecRange>
        <SocialPreferences
          roleUser="host"
          privacy={privacyHost}
          newInTown={newInTown}
        />
      </SocialPreferensecRange>
    </HostDetailsWrapper>
  );
};

const MapWithRoom: React.FC<{
  location: google.maps.LatLngLiteral;
  zoom: number;
}> = ({ location, zoom }) => {
  const handleClick = () => {
    window.open(generateGoogleMapsUrl({ location, zoom }), "_blank");
  };
  const { t } = useTranslation("host");

  return (
    <MapWithRoomRoot>
      <TitleH2>{t("hostProfile.mapWithRoom.title")}</TitleH2>
      <MapWithRoomImage
        onClick={handleClick}
        src={generateStaticMapImageUrl({
          location,
          zoom,
          width: 400,
          height: 400
        })}
        alt={t("hostProfile.mapWithRoom.imageTitle")}
      />
      <MapWithRoomImageCaption onClick={handleClick}>
        {t("hostProfile.mapWithRoom.imageCaption")}
      </MapWithRoomImageCaption>
    </MapWithRoomRoot>
  );
};
